/**
 * HQ Vocal Separation (hqstems) quotas & costs — the ONE place these live.
 * Used by the server API routes (app/api/hqstems/*) for enforcement and
 * importable client-side for display copy. Daily counters share the
 * feature-agnostic loopforge_consume/refund/quota_left RPCs (identity ×
 * UTC-date counters) via the identity prefixes below, so the quota day
 * resets at UTC midnight exactly like Loop Forge.
 */

/**
 * 30-second preview: 3/day for EVERYONE, including anonymous visitors.
 * Preview inference costs ~$0.001, so a generous free allowance is worth it to
 * let visitors actually hear the HQ quality before hitting the paid full run.
 */
export const HQSTEMS_PREVIEW_PER_DAY = 3;

/** Full-song runs per day for Pro subscribers (daily counter, no credits). */
export const HQSTEMS_PRO_FULL_PER_DAY = 15;

/** Credits consumed per full-song run for non-Pro (logged-in) users. */
export const HQSTEMS_FULL_CREDITS = 5;

/** Backend input limits (mirrors the Modal API's own validation). */
export const HQSTEMS_MAX_FILE_MB = 50;
export const HQSTEMS_MAX_DURATION_SEC = 600;
export const HQSTEMS_ACCEPTED_EXTENSIONS = ['mp3', 'wav', 'flac', 'm4a', 'ogg'] as const;

/**
 * Identity prefixes for the shared loopforge_usage counter table.
 * 'hqprev:user:<uuid>' / 'hqprev:ip:<hash>' etc. — the prefix namespaces
 * hqstems counters away from Loop Forge's own rows.
 */
export const HQSTEMS_PREVIEW_PREFIX = 'hqprev:';
export const HQSTEMS_FULL_PREFIX = 'hqfull:';

/** job_token lifetime (seconds): enough to start the upload, nothing more. */
export const HQSTEMS_TOKEN_TTL_SEC = 15 * 60;

/**
 * How long the refund route trusts a Modal "jti not found" answer to mean
 * "no job ever ran" (→ refundable). Modal sweeps a spent-token (jti) record
 * ~1h (JTI_MAX_AGE_SEC) after the job starts; once it's gone we can no longer
 * distinguish "never ran" from "ran, succeeded, then swept". A genuine
 * failed/never-ran refund always fires within minutes of authorize, so beyond
 * this window we fail closed rather than risk refunding a swept success. Keep
 * this comfortably below Modal's JTI_MAX_AGE_SEC and above the max legit
 * refund latency (HQ_POLL_TIMEOUT ~12 min).
 */
export const HQSTEMS_REFUND_VERIFY_WINDOW_SEC = 30 * 60;

export type HqStemsKind = 'full' | 'preview';

/** credit_transactions reasons (consume / idempotent refund). */
export const HQSTEMS_CONSUME_REASON = 'hqstems_full';
export const HQSTEMS_REFUND_REASON = 'hqstems_refund';
